import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import { useToast } from './ui/ToastProvider';
import { transactionStore, ManualSettlement } from '../utils/transactionStore';
const MakerManualSettlementTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingSettlements, setPendingSettlements] = useState<ManualSettlement[]>([]);
  const [rejectedSettlements, setRejectedSettlements] = useState<ManualSettlement[]>([]);
  const [selectedSettlement, setSelectedSettlement] = useState<ManualSettlement | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const refreshSettlements = () => {
    setPendingSettlements(transactionStore.getPendingManualSettlements());
    setRejectedSettlements(transactionStore.getRejectedManualSettlements());
  };
  useEffect(() => {
    refreshSettlements();
    const unsubscribe = transactionStore.subscribe(refreshSettlements);
    return unsubscribe;
  }, []);
  const handleAction = (settlement: ManualSettlement, action: 'resubmit' | 'discard') => {
    setSelectedSettlement(settlement);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selectedSettlement || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitManualSettlement(selectedSettlement.id, reason);
        showToast(`Manual settlement resubmitted for ${selectedSettlement.transactionId}`, 'success');
      } else {
        transactionStore.discardManualSettlement(selectedSettlement.id, reason);
        showToast(`Manual settlement discarded for ${selectedSettlement.transactionId}`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelectedSettlement(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const handleViewDetails = (settlement: ManualSettlement) => {
    setSelectedSettlement(settlement);
    setDetailsDrawerOpen(true);
  };
  const pendingColumns: Column<ManualSettlement>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1466" data-mp="1izxh9eqrn6mo">
      
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1467" data-mp="3rifm3aw9badg">
      
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1468" data-mp="3ihhgwpa30wta">
          {row.mtoName}
        </Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1469" data-mp="f3dddvmja92y">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1470" data-mp="56s4v0bb2fud">
          {row.beneficiaryName}
        </Typography>
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-1471" data-mp="2pwpw7447z4xq">
      
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1472" data-mp="8mjvm444oltr" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1473" data-mp="15ahstenp5148">
      
          {new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}
        </Typography>
  }];
  const rejectedColumns: Column<ManualSettlement>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1474" data-mp="3c4brhtldxpx8">
      
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1475" data-mp="34heq55n31cvk">
      
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1476" data-mp="3djxphfrnt7d2">
          {row.mtoName}
        </Typography>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1477" data-mp="2lt9pw85mwa1i">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'makerReason',
    label: 'Original Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1478" data-mp="2ur3ojpqvffru">
      
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1479" data-mp="601ove4x4dsn">
      
          {row.checkerComment}
        </Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1480" data-mp="kopg587qe1cs">
      
          {new Date(row.checkerActedAt || '').toLocaleString()}
        </Typography>
  }];
  const rejectedRowActions: RowAction<ManualSettlement>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => handleViewDetails(row)
  }, {
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Discard',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1481" data-mp="1cf3o6nt2cjgj">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1482" data-mp="1cizwxli3sn9m">
        
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1483" data-mp="2sxci56q0f8ju">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1485" data-mp="2o6dxxw1whqbb">
              
                <Typography variant="body2" fontWeight={500} data-id="element-1486" data-mp="2j5gezh92gs8r">
                  Pending
                </Typography>
                <Chip label={pendingSettlements.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1487" data-mp="299d2piki3d83" />
              
              </Box>} data-id="element-1484" data-mp="2szgt28mbt7tm" />
          
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1489" data-mp="3umpvbuwwf45u">
              
                <Typography variant="body2" fontWeight={500} data-id="element-1490" data-mp="33i5eej6un0q3">
                  Rejected
                </Typography>
                <Chip label={rejectedSettlements.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1491" data-mp="38i9ljilvaj3n" />
              
              </Box>} data-id="element-1488" data-mp="3fn7ndg9rg0d" />
          
        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending Manual Settlements" data={pendingSettlements} columns={pendingColumns} keyField="id" emptyStateMessage="No pending manual settlements" initialVisibleColumns={['transactionId', 'mtoName', 'amountFCY', 'beneficiaryName', 'makerReason', 'status', 'requestedAt']} onRowClick={handleViewDetails} data-id="element-1492" data-mp="2asy8gz52f8ut" /> : <DataTable title="Rejected Manual Settlements" data={rejectedSettlements} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected manual settlements" initialVisibleColumns={['transactionId', 'mtoName', 'amountFCY', 'makerReason', 'checkerComment', 'checkerActedAt']} onRowClick={handleViewDetails} data-id="element-1493" data-mp="1yiyq7ioxcp6b" />}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1494" data-mp="2cby6vo0g0pfg">
        
        <DialogTitle data-id="element-1495" data-mp="2m92vduhrgwzf">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1496" data-mp="2m8kq68d4f8fd">
            
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1497" data-mp="rua427nwusda" />
            
            <Typography variant="h6" fontWeight={600} data-id="element-1498" data-mp="1v8mq48ouczso">
              {actionType === 'resubmit' ? 'Resubmit' : 'Discard'} Manual
              Settlement
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1499" data-mp="2ivl5t45503pj">
          <Box sx={{
          mt: 1
        }} data-id="element-1500" data-mp="2iw4d7qx5v9pb">
            
            {selectedSettlement && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1501" data-mp="2dxjl6es2bwby">
                
                  Transaction ID:{' '}
                  <strong data-id="element-1502" data-mp="245421e5151ph">{selectedSettlement.transactionId}</strong>
                </Typography>

                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1503" data-mp="31o6zpiwhhk7h">
                
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1504" data-mp="395vclgz47pw1">
                  
                    Original Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1505" data-mp="3j4b3biu8cx50">
                  
                    {selectedSettlement.resubmitReason || selectedSettlement.makerReason}
                  </Typography>

                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1506" data-mp="rd1159yts7o">
                  
                    Checker's Rejection Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1507" data-mp="amelvox7k84g">
                  
                    {selectedSettlement.checkerComment}
                  </Typography>
                </Paper>
              </>}

            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Discarding'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'discarding'} this settlement...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1508" data-mp="3h3n8jwwxvekp" />
            
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1509" data-mp="doriehxru3g8">
          
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1510" data-mp="2kf7epomsqj1s">
            
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1512" data-mp="l8sc2pufcdg9" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1513" data-mp="l6fz6r3iwta2" />} data-id="element-1511" data-mp="2fhp9o9bwgnz2">
            
            {actionType === 'resubmit' ? 'Resubmit' : 'Discard'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Drawer */}
      {selectedSettlement && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedSettlement(null);
    }} transaction={{
      id: selectedSettlement.transactionId,
      mtoName: selectedSettlement.mtoName,
      mtoRef: `MTO-${selectedSettlement.transactionId}`,
      bankRef: `BNK-${selectedSettlement.transactionId}`,
      branchCode: '',
      agentCode: '',
      payoutType: selectedSettlement.payoutType,
      amountFCY: selectedSettlement.amountFCY,
      currencyFCY: selectedSettlement.currencyFCY,
      amountPKR: (parseFloat(selectedSettlement.amountFCY) * 280).toString(),
      corridor: 'PK-US',
      purposeCode: 'FAM',
      purposeText: 'Family Support',
      currentState: selectedSettlement.currentState as any,
      lifecycleStatus: 'FAILED' as any,
      createdAt: selectedSettlement.requestedAt,
      updatedAt: selectedSettlement.requestedAt,
      remitterName: 'Remitter Name',
      beneficiaryName: selectedSettlement.beneficiaryName,
      beneficiaryIBAN: selectedSettlement.beneficiaryIBAN,
      railRefs: {},
      stateHistory: []
    } as any} data-id="element-1514" data-mp="2aze81ynqtpq" />}
    </Box>;
};
export default MakerManualSettlementTable;